import React, { Ref, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import parse from 'html-react-parser';
import Highlighter from 'react-highlight-words';
import MixPanelWrapper from 'js/servises/MixPanelWrapper';
import Heading from 'js/components/Common/Heading/Heading';
import useFetchData from 'js/hooks/useFetchData/useFetchData';
import SearchService from 'js/containers/Pages/Search/SearchService';
import DataValidator from 'js/components/Common/DataValidator/DataValidator';

import fetchData from './fetchData';
import classes from './AutoComplete.module.pcss';
import SearchStore from 'js/store/SearchStore';
import { observer } from 'mobx-react';

export interface Props {
    query: string,
    focus: number | null,
    itemClick: any,
    itemChange: any,
    itemRef: Ref<any>,
    isHome?: boolean
}

export interface AutoCompleteTitles {
    title: string,
    id: number,
    search_count: number
}

const AutoComplete = ({ query, focus, itemClick, itemChange, itemRef, isHome = false }: Props) => {
    const {isAISearch} = SearchStore;
    const ulRef = useRef(null);
    const [titles, setTitles] = useState<AutoCompleteTitles[]>([]);
    const [displayedTitles, setDisplayedTitles] = useState<AutoCompleteTitles[]>([]);
    const sep = useMemo(() => /\W+/g, []);

    const sendMixPanelEvent = useCallback(async (value: string) => {
        await MixPanelWrapper.TrackEvent(
            'Auto complete is used',
            {
                value,
                category: 'Search Input',
                words: SearchService.calculateWords(value),
                characters: value?.length,
                searchType: isAISearch ? 'AI' : 'Regular'
            });
    }, [isAISearch]);

    const itemClickHandler = useCallback(async (e: any, id: number) => {
        itemClick(e, id);
        setTitles([]);
        SearchStore.setShowSearch(false);
        SearchStore.setSearchStatus(false);
        await sendMixPanelEvent(e?.currentTarget?.textContent);
    }, [sendMixPanelEvent, itemClick]);

    useEffect(() => {
        const ul = ulRef?.current;
        if (ul) {
            const el = ul?.querySelector('.active');
            const id = el?.getAttribute('data-key');
            itemChange(el, id);
        }
    }, [focus, itemChange]);

    const queryStr = useMemo(() => query, [query]);

    useEffect(() => {
        if (!query?.length) {
            setTitles([]);
            setDisplayedTitles([]);
        }
    }, [query]);

    useFetchData({
        query: fetchData(queryStr),
        condition: queryStr?.length > 0,
        updateData: (data, isData) => {
            if (isData) {
                return;
            }
            setTitles(data?.search?.autoCompleteResult);
            setDisplayedTitles(data?.search?.autoCompleteResult.slice(0, 10));
            SearchStore.setAtoCompleteSpinnerStatus(false);
        }
    });

    const autoCompleteShowVar = useMemo(() =>
            titles?.length > 0 && query?.length > 0,
        [titles, query]);

    const handleScroll = useCallback(() => {
        if (!ulRef.current) {
            return;
        }

        const { scrollTop, scrollHeight, clientHeight } = ulRef.current;
        const isBottomReached = Math.ceil(scrollTop + clientHeight) >= scrollHeight;

        if (isBottomReached) {
            const newItems = titles.slice(displayedTitles.length, displayedTitles.length + 5);
            setDisplayedTitles(prevItems => [...prevItems, ...newItems]);
        }
    }, [titles, displayedTitles]);

    useEffect(() => {
        const ulElement = ulRef.current;
        if (ulElement) {
            ulElement.addEventListener('scroll', handleScroll);
            return () => ulElement.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    return <DataValidator data={autoCompleteShowVar}>
        <div data-no-translate={true}  ref={itemRef} className={`${classes.AutoComplete} ${isHome ? classes.Home : ''} notranslate`}>
            <Heading text={'Suggested Searches'} type={'h4'} />
            <ul data-active-index={focus}
                ref={ulRef}>
                {displayedTitles?.map((item, index) => {
                    const { title } = item || {};
                    const rawTitle = parse(title);
                    return (
                        <li className={index === focus ? 'active' : ''}
                            data-key={item?.id}
                            key={item?.id}
                            onClick={e => itemClickHandler(e, item?.id)}
                        >
                            <Highlighter
                                highlightClassName={classes.Selected}
                                searchWords={[query]}
                                autoEscape={true}
                                caseSensitive={false}
                                className="HighlighterItem"
                                unhighlightClassName='Text'
                                textToHighlight={rawTitle}
                                findChunks={() =>
                                    SearchService.findChunksAtBeginningOfWords(rawTitle, query?.split(sep), sep)}
                            />
                        </li>
                    );
                })}
            </ul>
        </div>
    </DataValidator>;
};

export default observer(AutoComplete);
